import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Search, Clock, Gavel, 
    RefreshCw, Hash, Info, User, 
    CheckCircle2, XCircle, AlertCircle, ShoppingBag, Award
} from 'lucide-react';
import { adminApi } from '@/utils/api';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

export const RecordsManager = () => {
    const [stats, setStats] = useState<any>(null);
    const [records, setRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'shop' | 'auction' | 'brand'>('shop');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDetail, setSelectedDetail] = useState<any>(null);
    const { impactOccurred } = useHapticFeedback();

    const fetchStats = async () => {
        try {
            const res = await adminApi.getRecordsStats();
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (e) { console.error(e); }
    };

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi.getSalesRecords({ 
                type: activeTab, 
                status: statusFilter !== 'all' ? statusFilter : undefined 
            });
            const data = await res.json();
            if (data.success) setRecords(data.records);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [activeTab, statusFilter]);

    useEffect(() => {
        fetchStats();
        fetchRecords();
    }, [fetchRecords]);

    const filteredRecords = records.filter(r => 
        r.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.seller_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'sold': return <CheckCircle2 size={12} className="text-green-400" />;
            case 'completed': return <CheckCircle2 size={12} className="text-green-400" />;
            case 'active': return <Clock size={12} className="text-blue-400" />;
            case 'cancelled': return <XCircle size={12} className="text-red-400" />;
            default: return <AlertCircle size={12} className="text-white/40" />;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header Stats */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    { label: 'Shop', value: stats?.shop || 0, icon: ShoppingBag, color: '#3390EC' },
                    { label: 'Auctions', value: stats?.auctions || 0, icon: Gavel, color: '#F59E0B' },
                    { label: 'Brands', value: stats?.brands || 0, icon: Award, color: '#10B981' }
                ].map((s, i) => (
                    <div key={i} className="glass border border-white/5 p-4 rounded-3xl relative overflow-hidden group">
                        <div className="absolute -right-2 -top-2 opacity-5 scale-150 rotate-12 group-hover:opacity-10 transition-all">
                            <s.icon size={48} color={s.color} />
                        </div>
                        <div className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">{s.label}</div>
                        <div className="text-2xl font-black font-mono">{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Controls */}
            <div className="space-y-4">
                <div className="flex p-1 glass-inset rounded-2xl border border-white/5">
                    {(['shop', 'auction', 'brand'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(tab); impactOccurred('light'); }}
                            className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-[#3390EC] text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="flex gap-2">
                    <div className="flex-1 glass-inset border border-white/5 rounded-xl px-3 flex items-center gap-2 group focus-within:border-[#3390EC]/50 transition-all">
                        <Search size={16} className="text-white/20 group-focus-within:text-[#3390EC]" />
                        <input 
                            type="text" 
                            placeholder="Search username or seller..."
                            className="w-full bg-transparent py-2.5 text-sm focus:outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="glass-inset border border-white/5 rounded-xl px-4 text-xs font-bold focus:outline-none appearance-none cursor-pointer"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Listed</option>
                        <option value="sold">Sold</option>
                        {activeTab === 'auction' && <option value="bidding">Bidding</option>}
                        {activeTab === 'shop' && <option value="p2p">Contract/P2P</option>}
                    </select>
                </div>
            </div>

            {/* List */}
            <div className="glass-strong border border-white/5 rounded-3xl overflow-hidden shadow-2xl">
                <div className="max-h-[500px] overflow-y-auto divide-y divide-white/5 custom-scrollbar">
                    {loading ? (
                        <div className="p-12 text-center text-white/20 animate-pulse">
                            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                            <span className="text-xs font-bold uppercase">Decrypting Records...</span>
                        </div>
                    ) : filteredRecords.length > 0 ? (
                        filteredRecords.map((item) => (
                            <div 
                                key={item.id} 
                                onClick={() => { setSelectedDetail(item); impactOccurred('medium'); }}
                                className="p-4 flex items-center justify-between hover:bg-white/5 active:bg-white/10 transition-all cursor-pointer group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="glass-chip w-10 h-10 rounded-2xl flex items-center justify-center text-[#3390EC] border border-white/5 group-hover:scale-105 transition-transform">
                                        {activeTab === 'brand' ? <Award size={20} /> : <Hash size={20} />}
                                    </div>
                                    <div>
                                        <div className="text-sm font-black flex items-center gap-2">
                                            {item.username}
                                            {item.is_nft === 'true' && (
                                                <span className="text-[8px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded uppercase font-black tracking-tighter shadow-sm border border-purple-500/10">NFT</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-white/40 font-bold flex items-center gap-1.5 uppercase mt-0.5">
                                            <User size={10} className="text-white/20" /> {item.seller_name || 'Anonymous'}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-black font-mono text-green-400">
                                        {item.price} <span className="text-[9px] opacity-60">GRAM</span>
                                    </div>
                                    <div className="flex items-center justify-end gap-1.5 mt-1">
                                        <span className="text-[9px] font-black uppercase tracking-widest opacity-40">{item.status}</span>
                                        {getStatusIcon(item.status)}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="p-12 text-center text-white/20">
                            <Info size={32} className="mx-auto mb-2 opacity-10" />
                            <p className="text-xs font-bold uppercase tracking-widest">No matching records found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            <AnimatePresence>
                {selectedDetail && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end justify-center p-4"
                        onClick={() => setSelectedDetail(null)}
                    >
                        <motion.div 
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-lg glass-strong rounded-[32px] p-8 border border-white/10 shadow-2xl space-y-8"
                        >
                            <div className="flex items-center justify-between">
                                <h3 className="text-tg-hint uppercase text-xs font-bold tracking-widest flex items-center gap-2">
                                    <Info size={14} className="text-[#3390EC]" /> Detailed Asset Overview
                                </h3>
                                <button onClick={() => setSelectedDetail(null)} className="glass-chip w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white transition-colors">
                                    <XCircle size={20} />
                                </button>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="glass-chip w-20 h-20 rounded-3xl border border-white/5 flex items-center justify-center text-[#3390EC]">
                                    {activeTab === 'brand' ? <Award size={40} /> : <Hash size={40} />}
                                </div>
                                <div>
                                    <h4 className="text-3xl font-black tracking-tight">{selectedDetail.username}</h4>
                                    <p className="text-tg-hint font-bold text-sm">Listed {new Date(selectedDetail.created_at).toLocaleDateString()}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="glass-inset p-4 rounded-2xl border border-white/5">
                                    <div className="text-[9px] font-black text-white/40 uppercase mb-1">Financial State</div>
                                    <div className="text-xl font-black text-green-400 font-mono">{selectedDetail.price} GRAM</div>
                                </div>
                                <div className="glass-inset p-4 rounded-2xl border border-white/5">
                                    <div className="text-[9px] font-black text-white/40 uppercase mb-1">Market Status</div>
                                    <div className="text-xl font-black uppercase">{selectedDetail.status}</div>
                                </div>
                                <div className="glass-inset p-4 rounded-2xl border border-white/5">
                                    <div className="text-[9px] font-black text-white/40 uppercase mb-1">Asset Owner</div>
                                    <div className="text-sm font-bold truncate">@{selectedDetail.seller_name || 'n/a'}</div>
                                </div>
                                <div className="glass-inset p-4 rounded-2xl border border-white/5">
                                    <div className="text-[9px] font-black text-white/40 uppercase mb-1">Asset Type</div>
                                    <div className="text-sm font-bold uppercase">{selectedDetail.is_nft === 'true' ? 'Digital Asset (NFT)' : 'Direct Access (P2P)'}</div>
                                </div>
                            </div>

                            {selectedDetail.p2p_status && (
                                <div className="glass-chip border border-blue-500/20 p-4 rounded-2xl">
                                    <div className="flex items-center gap-2 mb-2">
                                        <RefreshCw size={14} className="text-blue-400 animate-spin" />
                                        <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Active P2P Deal Detected</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-bold">Deal Status: <span className="uppercase text-blue-300">{selectedDetail.p2p_status}</span></span>
                                        <span className="text-[10px] font-mono opacity-50">ID: #{selectedDetail.p2p_deal_id}</span>
                                    </div>
                                </div>
                            )}

                            <button 
                                onClick={() => setSelectedDetail(null)}
                                className="w-full py-4 bg-[#3390EC] hover:bg-[#3390EC]/90 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-[#3390EC]/20 active:scale-[0.98] transition-all"
                            >
                                Close Overview
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
